import * as dateFnsTz from '@date-fns/tz';
import type { MilestoneMap1 } from '../types/MilestoneMap1';
import { getIntervalsForMilestoneMap } from './getIntervalsForMilestoneMap';
import { assertIanaZone } from './zone';

/**
 * Map-zone resolution (SPEC.DomainLayer section 3.3).
 *
 * `confidence`:
 * - `confirmed`: `map.timeZone` is set.
 * - `inferred`: every surviving candidate zone yields identical column ends.
 * - `ambiguous`: candidates disagree, no zone reaches the threshold, or there is no data.
 *   Safe for reads only; writes must be refused.
 */
export type MapTimeZoneResult = {
  zone: string;
  source: 'map' | 'inferred' | 'default';
  confidence: 'confirmed' | 'inferred' | 'ambiguous';
  /** Competing zones (ambiguous results only). Best candidate first. */
  candidates?: string[];
};

/** Statuses only need their `forIntervalEnding` instant. */
export type ZoneInferenceStatus = { forIntervalEnding: string };

/** A zone must match at least this share of observations to be a candidate. */
export const INFERENCE_MATCH_THRESHOLD = 0.95;

/** Preferred canonical names, used for canonical choice and ties; then alphabetical. */
export const ZONE_PREFERENCE_LIST = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const DAY_MS = 86_400_000;
const END_OF_DAY_MS = DAY_MS - 1; // 23:59:59.999
const MIN_MS = 60_000;

let cachedZones: string[] | undefined;
function allZones(): string[] {
  if (!cachedZones) {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    const set = new Set<string>(supported ?? ZONE_PREFERENCE_LIST);
    // Some ICU builds list legacy aliases only (e.g. Asia/Calcutta), so make sure the
    // preference list is always evaluated.
    for (const z of ZONE_PREFERENCE_LIST) set.add(z);
    set.add('UTC');
    cachedZones = Array.from(set).sort();
  }
  return cachedZones;
}

function preferenceRank(zone: string): number {
  const i = ZONE_PREFERENCE_LIST.indexOf(zone);
  return i === -1 ? ZONE_PREFERENCE_LIST.length : i;
}

function comparePreference(a: string, b: string): number {
  return preferenceRank(a) - preferenceRank(b) || (a < b ? -1 : a > b ? 1 : 0);
}

// `tzOffset` is exported by the package root at runtime, but the root typings (`.ts` re-exports)
// do not resolve under this package's TS `moduleResolution: node`, and the `@date-fns/tz/tzOffset`
// subpath does not resolve in mm-web's Jest. So take it from the namespace import, typed here.
const tzOffset = (dateFnsTz as unknown as { tzOffset: (zone: string, date: Date) => number })
  .tzOffset;

/** UTC offset of `zone` at instant `t`, in minutes east of UTC. */
function zoneOffsetMinutes(zone: string, t: number): number {
  return tzOffset(zone, new Date(t));
}

/** Offset changes of `zone` in `[start, end]`, found by 2-day sampling and bisection to 1 ms. */
function offsetChanges(zone: string, start: number, end: number): string[] {
  const STEP = 2 * DAY_MS;
  const changes: string[] = [];
  let prevT = start;
  let prevOffset = zoneOffsetMinutes(zone, prevT);
  for (let t = start + STEP; ; t += STEP) {
    const at = Math.min(t, end);
    const offset = zoneOffsetMinutes(zone, at);
    if (offset !== prevOffset) {
      let lo = prevT;
      let hi = at;
      while (hi - lo > 1) {
        const mid = Math.floor((lo + hi) / 2);
        if (zoneOffsetMinutes(zone, mid) === prevOffset) lo = mid;
        else hi = mid;
      }
      changes.push(`${hi}:${offset}`);
    }
    prevT = at;
    prevOffset = offset;
    if (at >= end) break;
  }
  return changes;
}

const mod = (n: number, m: number) => ((n % m) + m) % m;

/**
 * Instant of a date-time string carrying an explicit `Z` or numeric offset, else undefined.
 * Bare dates (`2024-01-01`, from the web date picker) and offset-less date-times parse in the
 * runtime zone or as UTC midnight, so they carry no zone information.
 */
function explicitInstant(value: unknown): number | undefined {
  if (typeof value !== 'string' || !/T.*(Z|[+-]\d{2}(:?\d{2})?)$/i.test(value)) return undefined;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? undefined : t;
}

type Observation = { t: number; offsetMs: number; weight: number };

/**
 * Observations (instant, writer offset). A web-written status is local 23:59:59.999 in its
 * writer's zone, so offset = 23:59:59.999 - utcTimeOfDay(t). `startDate` adds one observation
 * (local midnight). Observations at UTC midnight or without an explicit offset are excluded; hourly maps contribute none.
 * Observations are de-duplicated by instant with a weight (the count at that instant).
 */
function collectObservations(
  map: MilestoneMap1,
  statuses: ZoneInferenceStatus[],
): { observations: Observation[]; total: number } {
  if (map.interval === 'hours') return { observations: [], total: 0 };
  const byInstant = new Map<number, Observation>();
  const add = (t: number, offsetMs: number) => {
    if (Number.isNaN(t)) return;
    const existing = byInstant.get(t);
    if (existing) existing.weight += 1;
    else byInstant.set(t, { t, offsetMs, weight: 1 });
  };
  for (const s of statuses) {
    const t = explicitInstant(s.forIntervalEnding);
    // Web ends are :59.999 of a minute (offsets are whole minutes); UTC midnight is excluded.
    if (t === undefined || mod(t, DAY_MS) === 0 || (t + 1) % MIN_MS !== 0) continue;
    add(t, END_OF_DAY_MS - mod(t, DAY_MS));
  }
  const start = explicitInstant(map.startDate);
  if (start !== undefined && mod(start, DAY_MS) !== 0) {
    add(start, -mod(start, DAY_MS));
  }
  const observations = Array.from(byInstant.values()).sort((a, b) => b.weight - a.weight);
  const total = observations.reduce((n, o) => n + o.weight, 0);
  return { observations, total };
}

/**
 * Per-zone matched weight, evaluated only at the de-duplicated instants. Observations are
 * visited heaviest first; evaluation stops once a zone can no longer reach `minMatched`.
 * Returns -1 for an abandoned zone. This is a pure optimisation: it never changes which zones
 * reach `minMatched`.
 */
function matchedWeight(
  zone: string,
  observations: Observation[],
  total: number,
  minMatched: number,
): number {
  let matched = 0;
  let remaining = total;
  for (const o of observations) {
    const zoneOffsetMs = zoneOffsetMinutes(zone, o.t) * MIN_MS;
    if (mod(zoneOffsetMs - o.offsetMs, DAY_MS) === 0) matched += o.weight;
    remaining -= o.weight;
    if (matched + remaining < minMatched) return -1;
  }
  return matched;
}

/**
 * Column ends only: writes target `end` and `period` resolves at local noon, so zones whose
 * columns differ only in start instants (e.g. Havana vs New York daily maps) are equivalent.
 */
function boundarySignature(map: MilestoneMap1, zone: string): string {
  return getIntervalsForMilestoneMap(map, map.interval, { timeZone: zone })
    .map((i) => i.end.getTime())
    .join(',');
}

/**
 * Signature of a zone's UTC-offset function over the map's range (padded, since standardising
 * moves boundaries up to a year outside `startDate..endDate`). Zones with equal signatures have
 * identical offsets, so boundaries are generated once per signature, not per zone.
 */
function offsetSignature(map: MilestoneMap1, zone: string): string {
  const day = DAY_MS;
  const start = new Date(map.startDate).getTime() - 400 * day;
  const end = new Date(map.endDate).getTime() + 400 * day;
  return `${zoneOffsetMinutes(zone, start)}|${offsetChanges(zone, start, end).join(',')}`;
}

/**
 * Resolves the map zone: `map.timeZone` (confirmed), else inferred from the statuses'
 * end-of-day instants, else UTC. See SPEC.DomainLayer section 3.3.
 *
 * The result is not cached here; the server layer may cache the inferred result.
 */
export function resolveMapTimeZone(
  map: MilestoneMap1,
  statuses: ZoneInferenceStatus[],
): MapTimeZoneResult {
  if (map.timeZone) {
    assertIanaZone(map.timeZone);
    return { zone: map.timeZone, source: 'map', confidence: 'confirmed' };
  }
  return inferMapTimeZone(map, statuses);
}

/** Inference only (ignores `map.timeZone`). */
export function inferMapTimeZone(
  map: MilestoneMap1,
  statuses: ZoneInferenceStatus[],
): MapTimeZoneResult {
  const { observations, total } = collectObservations(map, statuses);
  if (total === 0) {
    return { zone: 'UTC', source: 'default', confidence: 'ambiguous' };
  }

  const zones = allZones();
  const threshold = Math.ceil(total * INFERENCE_MATCH_THRESHOLD - 1e-9);

  // Phase 1: zones reaching the threshold.
  const survivors: Array<{ zone: string; matched: number }> = [];
  for (const zone of zones) {
    const matched = matchedWeight(zone, observations, total, threshold);
    if (matched >= threshold) survivors.push({ zone, matched });
  }

  const byBest = (a: { zone: string; matched: number }, b: { zone: string; matched: number }) =>
    b.matched - a.matched || comparePreference(a.zone, b.zone);

  if (survivors.length > 0) {
    survivors.sort(byBest);
    const representatives = new Map<string, string>();
    for (const c of survivors) {
      const sig = offsetSignature(map, c.zone);
      if (!representatives.has(sig)) representatives.set(sig, c.zone);
    }
    // One offset function means identical boundaries without generating any columns.
    const identical =
      representatives.size === 1 ||
      new Set(Array.from(representatives.values()).map((z) => boundarySignature(map, z))).size ===
        1;
    if (identical) {
      const canonical = survivors.map((c) => c.zone).sort(comparePreference)[0];
      return { zone: canonical, source: 'inferred', confidence: 'inferred' };
    }
    return {
      zone: survivors[0].zone,
      source: 'inferred',
      confidence: 'ambiguous',
      candidates: survivors.map((c) => c.zone),
    };
  }

  // Phase 2: nothing reaches the threshold. Take the zone(s) matching the most observations.
  let best = 0;
  let top: Array<{ zone: string; matched: number }> = [];
  for (const zone of zones) {
    const matched = matchedWeight(zone, observations, total, best);
    if (matched < 0) continue;
    if (matched > best) {
      best = matched;
      top = [{ zone, matched }];
    } else if (matched === best) {
      top.push({ zone, matched });
    }
  }
  top.sort(byBest);
  if (top.length === 0) {
    return { zone: 'UTC', source: 'default', confidence: 'ambiguous' };
  }
  return {
    zone: top[0].zone,
    source: 'inferred',
    confidence: 'ambiguous',
    candidates: top.map((c) => c.zone),
  };
}
